import React, { useMemo } from 'react';
import { Badge, Button, Dropdown, Empty } from 'antd';
import { BellOutlined, UserOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useUnreadChat from '../../hooks/useUnreadChat';
import './support-bell.scss';

const timeAgo = (value, t) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return t('justNow', { defaultValue: 'just now' });
  if (min < 60) return `${min} min`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} h`;
  return new Date(value).toLocaleDateString([], { day: 'numeric', month: 'short' });
};

/**
 * Support inbox bell for the dashboard navbars.
 * Shows the unread badge and a dropdown of conversations, newest activity first.
 */
export default function SupportBell({ chatPath = '/dashboard/chat' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { count, conversations } = useUnreadChat();

  const items = useMemo(() => {
    const header = {
      key: '__header',
      type: 'group',
      label: (
        <div className="support-bell__head">
          <span>{t('supportChat', { defaultValue: 'Support Chat' })}</span>
          {count > 0 && <span className="support-bell__head-count">{count}</span>}
        </div>
      ),
    };

    if (!conversations.length) {
      return [
        header,
        {
          key: '__empty',
          disabled: true,
          label: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('noConversation', { defaultValue: 'No active conversation' })}
            />
          ),
        },
      ];
    }

    const rows = conversations.slice(0, 6).map((c) => ({
      key: String(c.id),
      label: (
        <div className={`support-bell__row${c.unread > 0 ? ' is-unread' : ''}`}>
          <span className="support-bell__avatar">
            {c.participant?.role === 'superadmin' || c.participant?.role === 'admin' ? (
              <SafetyCertificateOutlined />
            ) : (
              <UserOutlined />
            )}
          </span>
          <span className="support-bell__body">
            <span className="support-bell__top">
              <span className="support-bell__name">
                {c.with_superadmin ? t('supportChat', { defaultValue: 'Support' }) : c.title}
              </span>
              <span className="support-bell__time">
                {timeAgo(c.last_message?.created_at || c.updated_at, t)}
              </span>
            </span>
            <span className="support-bell__preview">
              {c.last_message?.body
                || (c.unread > 0
                  ? t('newMessage', { defaultValue: 'New message' })
                  : t('noMessages', { defaultValue: 'No messages yet.' }))}
            </span>
          </span>
          {c.unread > 0 && <span className="support-bell__dot">{c.unread}</span>}
        </div>
      ),
    }));

    return [
      header,
      ...rows,
      { type: 'divider', key: '__div' },
      {
        key: '__all',
        label: <span className="support-bell__all">{t('View all', { defaultValue: 'View all' })}</span>,
      },
    ];
  }, [conversations, count, t]);

  const onClick = ({ key }) => {
    if (key.startsWith('__')) {
      if (key === '__all') navigate(chatPath);
      return;
    }
    navigate(chatPath);
  };

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="support-bell__overlay"
      menu={{ items, onClick }}
    >
      <Button
        type="text"
        className="support-bell__trigger"
        aria-label={t('supportChat', { defaultValue: 'Support Chat' })}
        icon={
          <Badge count={count} size="small" overflowCount={99}>
            <BellOutlined />
          </Badge>
        }
      />
    </Dropdown>
  );
}
